/**
 * ParentSessionContext — управление сессией родителя в ЛК.
 *
 * Использует тот же endpoint /api/auth что и CRM (общая система сессий).
 * Cookie session_token ставится бэкендом, в JS не доступен (httpOnly).
 */
import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

const ParentSessionContext = createContext(null);

export const ParentSessionProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkSession = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch('/api/auth/me', { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        if (data.role === 'PARENT') {
          setUser(data);
        } else {
          // Залогинен, но не родитель — выгоняем
          setUser(null);
        }
      } else {
        setUser(null);
      }
    } catch (e) {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { checkSession(); }, [checkSession]);

  const login = useCallback(async (email, password) => {
    const res = await fetch('/api/auth/login', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ email, password, remember_me: true }),
    });
    if (!res.ok) {
      const err = await res.json();
      throw new Error(err.detail || 'Ошибка входа');
    }
    const data = await res.json();
    if (data.user.role !== 'PARENT') {
      // Если зашёл админ — выгоняем
      await logout();
      throw new Error('Этот аккаунт не является аккаунтом родителя');
    }
    setUser(data.user);
    return data.user;
  }, []);

  const logout = useCallback(async () => {
    try {
      await fetch('/api/auth/logout', { method: 'POST', credentials: 'include' });
    } catch {}
    setUser(null);
  }, []);

  const changePassword = useCallback(async (currentPassword, newPassword) => {
    try {
      const res = await fetch('/api/auth/change-password', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          current_password: currentPassword,
          new_password: newPassword,
        }),
      });

      if (!res.ok) {
        let detail = 'Не удалось сменить пароль';
        try {
          const err = await res.json();
          detail = err.detail || detail;
        } catch {}
        if (res.status === 401 || detail === 'Current password is incorrect') {
          return { success: false, error: 'Неверный текущий пароль' };
        }
        return { success: false, error: detail };
      }

      // Снимаем флаг must_change_password в state
      setUser((prev) => (prev ? { ...prev, must_change_password: false } : prev));
      return { success: true };
    } catch (e) {
      return { success: false, error: 'Ошибка сети. Попробуйте ещё раз.' };
    }
  }, []);

  const value = {
    user,
    loading,
    login,
    logout,
    changePassword,
    refresh: checkSession,
  };

  return (
    <ParentSessionContext.Provider value={value}>
      {children}
    </ParentSessionContext.Provider>
  );
};

export const useParentSession = () => {
  const ctx = useContext(ParentSessionContext);
  if (!ctx) {
    throw new Error('useParentSession must be used within ParentSessionProvider');
  }
  return ctx;
};
